import { gql, useApolloClient, useLazyQuery, useSubscription } from '@apollo/client';
import {
  Block,
  BlockCommodityType,
  Cable,
  CableCode,
  EquipmentCode,
  FireArea,
  Jumper,
  MetadataTableName,
  OperationType,
  OperatorType,
  Project,
  Raceway,
  RacewayCode,
  SearchMetadataColumnResult,
  SearchMetadataTableResult,
  SearchUiMessageResult,
  SearchUiTextResult,
  SubscriptionPayload,
  UserAccount,
} from '@bechtel/sr-types';
import { forEach, get } from 'lodash';
import { useEffect, useState } from 'react';
import { USER_PROJECT_ID } from '~/components';
import {
  GET_CABLE_BY_IDS,
  GET_CABLE_CODE_BY_IDS,
  GET_EQUIPMENT_CODES_BY_IDS,
  GET_FIRE_AREA_BY_IDS,
  GET_JUMPER_BY_IDS,
  GET_PROJECT_BY_ID,
  GET_RACEWAY_BY_IDS,
  GET_RACEWAY_CODE_BY_IDS,
  GET_USER_ACCOUNT_BY_AUTH,
  SEARCH_METADATA_COLUMNS_UI,
  SEARCH_METADATA_TABLES,
  SEARCH_UI_MESSAGE,
  SEARCH_UI_TEXTS,
  SUBSCRIPTION_ENTITIES_AFFECTED,
} from '~/gql';
import { GET_BLOCKS_BY_COMMODITY } from '~/gql/block';
import {
  clearCableCache,
  clearCableCodeCache,
  clearColorCodeCache,
  clearEquipmentCache,
  clearEquipmentCodeCache,
  clearFireAreaCache,
  clearJumperCache,
  clearRacewayCache,
  clearRacewayCodeCache,
} from '~/utilities';
import { useEquipmentByIdsQuery } from '../use-equipment-by-ids';

export interface UseEntityAffectedSubscriptionResult {
  name: string;
  items: any;
  operationType?: OperationType;
}

const getFragmentByName = (name: string, id: string) => {
  if (name === MetadataTableName.EQUIPMENT) {
    return {
      id: `Equipment:${id}`,
      fragment: gql`
        fragment equipment on Equipment {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.EQUIPMENT_CODE) {
    return {
      id: `EquipmentCode:${id}`,
      fragment: gql`
        fragment equipmentCode on EquipmentCode {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.RACEWAY) {
    return {
      id: `Raceway:${id}`,
      fragment: gql`
        fragment raceway on Raceway {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.RACEWAY_CODE) {
    return {
      id: `RacewayCode:${id}`,
      fragment: gql`
        fragment racewayCode on RacewayCode {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.CABLE_CODE) {
    return {
      id: `CableCode:${id}`,
      fragment: gql`
        fragment cableCode on CableCode {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.CABLE) {
    return {
      id: `Cable:${id}`,
      fragment: gql`
        fragment cable on Cable {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.FIRE_AREA) {
    return {
      id: `FireArea:${id}`,
      fragment: gql`
        fragment fireArea on FireArea {
          id
        }
      `,
    };
  } else if (name === MetadataTableName.PROJECT) {
    return {
      id: `Project:${id}`,
      fragment: gql`
        fragment project on Project {
          id
        }
      `,
    };
  } else if (name === MetadataTableName.JUMPER) {
    return {
      id: `Jumper:${id}`,
      fragment: gql`
        fragment jumper on Jumper {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.METADATA_COLUMN) {
    return {
      id: `MetadataColumn:${id}`,
      fragment: gql`
        fragment metadataColumn on MetadataColumns {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.METADATA_TABLE) {
    return {
      id: `MetadataTable:${id}`,
      fragment: gql`
        fragment metadataTable on MetadataTables {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.UI_CLIENT_TEXT) {
    return {
      id: `UiText:${id}`,
      fragment: gql`
        fragment uiText on UiTexts {
          id
          updatedTime
        }
      `,
    };
  } else if (name === MetadataTableName.UI_SERVER_MESSAGE) {
    return {
      id: `UiMessage:${id}`,
      fragment: gql`
        fragment uiMessage on UiMessages {
          id
          updatedTime
        }
      `,
    };
  }
  return null;
};

export function useEntityAffectedSubscription(): UseEntityAffectedSubscriptionResult | undefined {
  const [getEquipmentByIds, { data: equipmentsData }] = useEquipmentByIdsQuery(true);
  const [getEquipmentCodesById, { data: equipmentCodesData }] = useLazyQuery<{
    equipmentCodeByIds: EquipmentCode[];
  }>(GET_EQUIPMENT_CODES_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getBlocksByCommodityId, { data: blocksData }] = useLazyQuery<{
    blocksByCommodity: Block[];
  }>(GET_BLOCKS_BY_COMMODITY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getRacewayByIds, { data: racewaysData }] = useLazyQuery<{
    racewayByIds: Raceway[];
  }>(GET_RACEWAY_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getRacewayCodeByIds, { data: racewayCodesData }] = useLazyQuery<{
    racewayCodeByIds: RacewayCode[];
  }>(GET_RACEWAY_CODE_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getCableCodeByIds, { data: cableCodesData }] = useLazyQuery<{
    cableCodeByIds: CableCode[];
  }>(GET_CABLE_CODE_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getProjectById] = useLazyQuery<{
    projectById: Project;
  }>(GET_PROJECT_BY_ID, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getFireAreaByIds, { data: fireAreasData }] = useLazyQuery<{
    fireAreaByIds: FireArea[];
  }>(GET_FIRE_AREA_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getCableByIds, { data: cablesData }] = useLazyQuery<{
    cableByIds: Cable[];
  }>(GET_CABLE_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [getUserAccountByAuth, { data: userAccountData }] = useLazyQuery<{ userAccountByAuth: UserAccount }>(
    GET_USER_ACCOUNT_BY_AUTH,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );
  const [getJumperByIds, { data: jumpersData }] = useLazyQuery<{
    jumperByIds: Jumper[];
  }>(GET_JUMPER_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [searchMetadataColumns, { data: metadataColumns }] = useLazyQuery<{
    searchMetadataColumns: SearchMetadataColumnResult;
  }>(SEARCH_METADATA_COLUMNS_UI, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [searchTableMetadata, { data: tableMetadata }] = useLazyQuery<{
    searchMetadataTables: SearchMetadataTableResult;
  }>(SEARCH_METADATA_TABLES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [searchUIMessages, { data: uiMessagesData }] = useLazyQuery<{
    searchUiMessages: SearchUiMessageResult;
  }>(SEARCH_UI_MESSAGE, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const [searchUITexts, { data: uiTextsData }] = useLazyQuery<{
    searchUiTexts: SearchUiTextResult;
  }>(SEARCH_UI_TEXTS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const [entityInfo, setEntityInfo] = useState<UseEntityAffectedSubscriptionResult>();
  const client = useApolloClient();

  const { data } = useSubscription<{
    entitiesAffected: SubscriptionPayload;
  }>(SUBSCRIPTION_ENTITIES_AFFECTED);

  // Equipments
  useEffect(() => {
    if (!equipmentsData) return;
    setEntityInfo({
      name: MetadataTableName.EQUIPMENT,
      items: get(equipmentsData, 'equipmentByIds', []),
    });
    // when equipment is updated, clear the stats and count cache
    clearEquipmentCache(client.cache);
  }, [equipmentsData, client.cache]);

  // Equipment Codes
  useEffect(() => {
    if (!equipmentCodesData) return;
    setEntityInfo({
      name: MetadataTableName.EQUIPMENT_CODE,
      items: get(equipmentCodesData, 'equipmentCodeByIds', []),
    });
    clearEquipmentCodeCache(client.cache);
  }, [client.cache, equipmentCodesData]);

  // Cable Codes
  useEffect(() => {
    if (!cableCodesData) return;
    setEntityInfo({
      name: MetadataTableName.CABLE_CODE,
      items: get(cableCodesData, 'cableCodeByIds', []),
    });
    clearCableCodeCache(client.cache);
  }, [cableCodesData, client.cache]);

  // User Account Auth
  useEffect(() => {
    if (!userAccountData) return;
    setEntityInfo({
      name: MetadataTableName.USER_ACCOUNT,
      items: get(userAccountData, 'getUserAccountByAuth', []),
    });
  }, [userAccountData, client.cache]);

  // Raceways
  useEffect(() => {
    if (!racewaysData) return;
    setEntityInfo({
      name: MetadataTableName.RACEWAY,
      items: get(racewaysData, 'racewayByIds', []),
    });
    clearRacewayCache(client.cache);
  }, [client.cache, racewaysData]);

  // Raceway Codes
  useEffect(() => {
    if (!racewayCodesData) return;
    setEntityInfo({
      name: MetadataTableName.RACEWAY_CODE,
      items: get(racewayCodesData, 'racewayCodeByIds', []),
    });
    clearRacewayCodeCache(client.cache);
  }, [client.cache, racewayCodesData]);

  // Fire Areas
  useEffect(() => {
    if (!fireAreasData) return;
    setEntityInfo({
      name: MetadataTableName.FIRE_AREA,
      items: get(fireAreasData, 'fireAreaByIds', []),
    });
  }, [fireAreasData]);

  useEffect(() => {
    if (!blocksData) return;
    setEntityInfo({
      name: MetadataTableName.EQUIPMENT_BLOCK,
      items: get(blocksData, 'blocksByCommodity', []),
    });
  }, [blocksData]);
  // Jumpers
  useEffect(() => {
    if (!jumpersData) return;
    setEntityInfo({
      name: MetadataTableName.JUMPER,
      items: get(jumpersData, 'jumperByIds', []),
    });
    clearJumperCache(client.cache);
  }, [client.cache, jumpersData]);

  // Cables
  useEffect(() => {
    if (!cablesData) return;
    setEntityInfo({
      name: MetadataTableName.CABLE,
      items: get(cablesData, 'cableByIds', []),
    });
    clearCableCache(client.cache);
  }, [cablesData, client.cache]);

  // Metadata Columns
  useEffect(() => {
    if (!metadataColumns) return;
    setEntityInfo({
      name: MetadataTableName.METADATA_COLUMN,
      items: get(metadataColumns, 'searchMetadataColumns.items', []),
    });
  }, [metadataColumns]);

  // Table MetaData
  useEffect(() => {
    if (!tableMetadata) return;
    setEntityInfo({
      name: MetadataTableName.METADATA_TABLE,
      items: get(tableMetadata, 'searchMetadataTables.items', []),
    });
  }, [tableMetadata]);

  // UI Message
  useEffect(() => {
    if (!uiMessagesData) return;
    setEntityInfo({
      name: MetadataTableName.UI_MESSAGE,
      items: get(uiMessagesData, 'searchUiMessages.items', []),
    });
  }, [uiMessagesData]);

  // UI Text
  useEffect(() => {
    if (!uiTextsData) return;
    setEntityInfo({
      name: MetadataTableName.UI_TEXT,
      items: get(uiTextsData, 'searchUiTexts.items', []),
    });
  }, [uiTextsData]);

  // Entity item is added or deleted
  useEffect(() => {
    const userProjectId = window.sessionStorage.getItem(USER_PROJECT_ID);
    if (
      !data ||
      !data.entitiesAffected ||
      !data.entitiesAffected.name ||
      !data.entitiesAffected.entities ||
      !(
        data.entitiesAffected.operationType == OperationType.CREATE ||
        data.entitiesAffected.operationType == OperationType.DELETE
      ) ||
      !data.entitiesAffected.projectId ||
      data.entitiesAffected.projectId !== userProjectId
    )
      return;

    const { cache } = client;
    const { name, projectId } = data.entitiesAffected;

    if (!name || !projectId || projectId != userProjectId) return;

    // Delete the search entity cache, page cont and stats
    if (name === MetadataTableName.EQUIPMENT) {
      clearEquipmentCache(client.cache);
      cache.evict({ fieldName: 'searchEquipments' });
      cache.gc();
    } else if (name === MetadataTableName.EQUIPMENT_CODE) {
      clearEquipmentCodeCache(client.cache);
      cache.evict({ fieldName: 'searchEquipmentCodes' });
      cache.gc();
    } else if (name === MetadataTableName.RACEWAY) {
      clearRacewayCache(client.cache);
      cache.evict({ fieldName: 'searchRaceways' });
      cache.gc();
    } else if (name === MetadataTableName.RACEWAY_CODE) {
      clearRacewayCodeCache(client.cache);
      cache.evict({ fieldName: 'searchRacewayCodes' });
      cache.gc();
    } else if (name === MetadataTableName.CABLE_CODE) {
      clearCableCodeCache(client.cache);
      cache.evict({ fieldName: 'searchCableCodes' });
      cache.gc();
    } else if (name === MetadataTableName.JUMPER) {
      clearJumperCache(client.cache);
      cache.evict({ fieldName: 'searchJumpers' });
      cache.gc();
    } else if (name === MetadataTableName.CABLE) {
      clearCableCache(client.cache);
      cache.evict({ fieldName: 'searchCables' });
      cache.gc();
    } else if (name === MetadataTableName.FIRE_AREA) {
      clearFireAreaCache(client.cache);
      cache.evict({ fieldName: 'searchFireAreas' });
      cache.gc();
    } else if (name === MetadataTableName.COLOR_CODE) {
      clearColorCodeCache(client.cache);
      cache.evict({ fieldName: 'searchColorCodes' });
      cache.gc();
    }
  }, [client, data]);

  // Entity item is updated
  useEffect(() => {
    if (
      !data ||
      !data.entitiesAffected ||
      !data.entitiesAffected.name ||
      !data.entitiesAffected.entities ||
      data.entitiesAffected.operationType !== OperationType.UPDATE
    )
      return;
    const cachedCommodityIds: string[] = [];
    // Fetch only those entities which are already cached
    forEach(data.entitiesAffected.entities, ({ id, updatedTime }) => {
      if (
        data.entitiesAffected.name === MetadataTableName.EQUIPMENT_BLOCK ||
        data.entitiesAffected.name === MetadataTableName.EQUIPMENT_CODE_BLOCK
      ) {
        getBlocksByCommodityId({
          variables: {
            commodityId: data.entitiesAffected.entities[0].id,
            blockCommodityType:
              data.entitiesAffected.name === MetadataTableName.EQUIPMENT_BLOCK
                ? BlockCommodityType.Equipment
                : BlockCommodityType.EquipmentCode,
          },
        });
        return;
      }

      const fragment = getFragmentByName(data?.entitiesAffected?.name, id);
      if (!fragment) return;
      const cachedCommodity = client.readFragment(fragment);
      // Updated time check to bypass if the real time notification is from initiator
      if (cachedCommodity && cachedCommodity.updatedTime !== updatedTime) {
        cachedCommodityIds.push(id);
      }
    });

    if (!cachedCommodityIds || cachedCommodityIds.length === 0) return;

    // Equipment by ids
    if (data.entitiesAffected.name === MetadataTableName.EQUIPMENT) {
      getEquipmentByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.EQUIPMENT_CODE) {
      getEquipmentCodesById({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.RACEWAY) {
      getRacewayByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.RACEWAY_CODE) {
      getRacewayCodeByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.CABLE_CODE) {
      getCableCodeByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.FIRE_AREA) {
      getFireAreaByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.PROJECT) {
      getProjectById({
        variables: {
          id: cachedCommodityIds[0],
        },
      });
      getUserAccountByAuth();
    } else if (data.entitiesAffected.name === MetadataTableName.JUMPER) {
      getJumperByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.CABLE) {
      getCableByIds({
        variables: {
          ids: cachedCommodityIds,
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.METADATA_COLUMN) {
      searchMetadataColumns({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  field: 'id',
                  operator: OperatorType.IN,
                  values: cachedCommodityIds,
                },
              ],
            },
          },
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.METADATA_TABLE) {
      searchTableMetadata({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  field: 'id',
                  operator: OperatorType.IN,
                  values: cachedCommodityIds,
                },
              ],
            },
          },
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.UI_CLIENT_TEXT) {
      searchUITexts({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  field: 'id',
                  operator: OperatorType.IN,
                  values: cachedCommodityIds,
                },
              ],
            },
          },
        },
      });
    } else if (data.entitiesAffected.name === MetadataTableName.UI_SERVER_MESSAGE) {
      searchUIMessages({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  field: 'id',
                  operator: OperatorType.IN,
                  values: cachedCommodityIds,
                },
              ],
            },
          },
        },
      });
    }
  }, [
    client,
    data,
    getEquipmentByIds,
    getEquipmentCodesById,
    getCableCodeByIds,
    getRacewayByIds,
    getRacewayCodeByIds,
    getFireAreaByIds,
    getProjectById,
    getBlocksByCommodityId,
    getJumperByIds,
    getCableByIds,
    searchMetadataColumns,
    searchTableMetadata,
    searchUITexts,
    searchUIMessages,
    getUserAccountByAuth,
  ]);

  return entityInfo;
}
